import React, { useState, useEffect } from 'react';
import dropletImage from '../../public/assets/droplet.webp';

interface Droplet {
  id: number;
  left: number;
  size: number;
  duration: number;
  delay: number;
}

let dropletId = 0; // Unique identifier for each droplet

interface Props {
  isWatering: boolean;
}

const WateringAnimation: React.FC<Props> = ({ isWatering }) => {
  const [droplets, setDroplets] = useState<Droplet[]>([]);

  // Start or stop the animation based on isWatering prop
  useEffect(() => {
    if (isWatering && droplets.length === 0) {
      triggerWateringAnimation();
    } else if (!isWatering && droplets.length > 0) {
      setDroplets([]); // Clear all droplets when not watering
    }
  }, [isWatering]);

  const triggerWateringAnimation = () => {
    const numberOfDropletsPerWave = 15;
    const numberOfWaves = 3;
    const waveInterval = 500;

    for (let wave = 0; wave < numberOfWaves; wave++) {
      setTimeout(() => {
        if (!isWatering) return; // Don't add new droplets if we stopped watering

        const newDroplets: Droplet[] = [];
        const containerWidth = 300;

        const usedPositions: number[] = [];

        for (let i = 0; i < numberOfDropletsPerWave; i++) {
          // Randomize size between 10px and 30px
          const size = Math.random() * 20 + 10; // 10 to 30

          // Ensure no overlap by checking used positions
          let posX: number;
          let attempts = 0;
          do {
            posX = Math.random() * (containerWidth - size);
            attempts++;
          } while (
            usedPositions.some((pos) => Math.abs(pos - posX) < size) &&
            attempts < 100
          );

          usedPositions.push(posX);

          const duration = Math.random() * 1 + 2; // 2 to 3 seconds

          // Randomize delay for fade in and fade out
          const delay = Math.random() * 0.5; // 0 to 0.5 seconds

          newDroplets.push({
            id: dropletId++,
            left: posX,
            size,
            duration,
            delay,
          });
        }

        setDroplets((prev) => [...prev, ...newDroplets]);
      }, wave * waveInterval);
    }
  };

  const handleAnimationEnd = (id: number) => {
    setDroplets((prev) =>
      prev.map((droplet) => {
        if (droplet.id === id) {
          return {
            ...droplet,
            delay: 0, // Reset delay
            duration: Math.random() * 1 + 2, // New random duration
          };
        }
        return droplet;
      })
    );
  };

  return (
    <div className='absolute top-0 z-20 flex h-full w-full items-start justify-center bg-transparent'>
      <div
        className='relative h-[70%] w-[300px] overflow-hidden bg-transparent bg-cover bg-center'
        style={{ pointerEvents: 'none' }}
      >
        {droplets.map((droplet) => (
          <div
            key={droplet.id}
            className='absolute top-[-100vh] bg-no-repeat'
            style={{
              left: `${droplet.left}px`,
              width: `${droplet.size}px`,
              height: `${droplet.size}px`,
              backgroundImage: `url(${dropletImage})`,
              backgroundSize: 'contain',
              animation: `fall ${droplet.duration}s linear ${droplet.delay}s infinite`,
            }}
            onAnimationEnd={() => handleAnimationEnd(droplet.id)}
          ></div>
        ))}
      </div>
    </div>
  );
};

// Add keyframes animation
const fallAnimation = `
  @keyframes fall {
    0% {
      transform: translateY(0) scale(1);
      opacity: 0;
    }
    10% {
      opacity: 0.7;
    }
    80% {
      opacity: 0.7;
    }
    100% {
      transform: translateY(200vh) scale(1.5);
      opacity: 0;
    }
  }
`;

// Add style tag to head
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = fallAnimation;
  document.head.appendChild(style);
}

export default WateringAnimation;
